import React, { useState, useEffect } from "react";
import {
  Button,
  Box,
  useColorModeValue,
  Heading,
  Text,
  Badge,
} from "@chakra-ui/react";
import { db } from "../firebase.config";
import { collection, getDocs } from "firebase/firestore";
import { Link } from "react-router-dom";
import { ScaleLoader } from "react-spinners";
import { ImLocation } from "react-icons/im";
import { FaStore } from "react-icons/fa";
import moment from "moment";
import { Helmet } from "react-helmet";

const SeeAllFeaturedSellers = () => {
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const cardBg = useColorModeValue("white", "gray.800");
  const shadowColor = useColorModeValue(
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    "0 4px 6px -1px rgba(0, 0, 0, 0.3), 0 2px 4px -1px rgba(0, 0, 0, 0.2)"
  );

  useEffect(() => {
    const fetchSellers = async () => {
      try {
        setLoading(true);
        const carsRef = collection(db, "Feature-Seller");
        const qSnap = await getDocs(carsRef);
        const sellersArray = [];
        qSnap.forEach((doc) => {
          sellersArray.push({ id: doc.id, ...doc.data() });
        });
        setSellers(sellersArray);
        setLoading(false);
      } catch (error) {
        setLoading(false);
        setError("Failed to load featured sellers");
      }
    };

    fetchSellers();
  }, []);

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ScaleLoader color="#3182ce" />
      </div>
    );

  if (error)
    return (
      <Box className="text-center p-8">
        <Heading size="lg" className="text-red-500 mb-4">
          {error}
        </Heading>
        <Link to="/" className="text-blue-500 hover:underline">
          Return to homepage
        </Link>
      </Box>
    );

  if (sellers.length === 0)
    return (
      <Box className="text-center p-8">
        <Heading size="lg" className="text-gray-600 mb-4">
          No featured showrooms available
        </Heading>
        <Link to="/" className="text-blue-500 hover:underline">
          Browse other sellers
        </Link>
      </Box>
    );

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <Helmet>
        <title>Featured Showrooms | NCP Wheels</title>
        <meta
          name="keywords"
          content="ncp cars, non custom cars, ncp car, non custom paid cars, ncp cars for sale in swat, ncp cars for sale in swat pakistan, ncp car for sale in swat, ncp cars for sale in pakistan, ncp cars in pakistan for sale, ncp car for sale in pakistan, pakistan non custom cars for sale, non custom car for sale in pakistan, non custom cars in pakistan for sale, non custom cars for sale in pakistan, ncp car for sale, ncp cars for sale in swat olx, ncp cars for sale in chaman, ncp cars in swat, non custom paid cars in chaman, chaman border cars, ncp cars swat, swat ncp cars, non custom paid cars chaman, non custom cars in chaman, chaman non custom cars, chaman border non custom cars price, ncp cars in pakistan, cars for sale in swat, ncp cars swat chakdara, ncp swat chakdara motors, car for sale in swat, chaman border cars for sale in pakistan, non custom car price in chaman"
        />
      </Helmet>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <Heading as="h1" size="xl" className="text-gray-800 mb-2">
            Premium Auto Dealers
          </Heading>
          <div className="w-24 h-1 bg-blue-500 mx-auto mb-6"></div>
          <Text fontSize="lg" className="text-gray-600 max-w-2xl mx-auto">
            Explore our network of trusted and verified automotive showrooms
          </Text>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
          {sellers.map((seller) => (
            <Box
              key={seller.id}
              className={`bg-${cardBg} rounded-xl overflow-hidden border border-opacity-20 border-gray-300 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl`}
              style={{ boxShadow: shadowColor }}
            >
              {/* Image with hover effect */}
              <div className="relative group overflow-hidden h-48">
                <img
                  src={seller.thumbnail || "/placeholder-showroom.jpg"}
                  alt={seller.sellerName}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                {/* Badges */}
                <div className="absolute top-3 right-3 flex space-x-2">
                  <Badge colorScheme="blue" className="shadow-md">
                    Featured
                  </Badge>
                  {seller?.timestamp && (
                    <Badge colorScheme="green" fontSize="xs">
                      {moment(seller?.timestamp?.toDate()).fromNow()}
                    </Badge>
                  )}
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-start mb-3">
                  <FaStore className="text-blue-500 mt-1 mr-2" size={18} />
                  <div>
                    <Heading as="h3" size="md" className="text-gray-900">
                      {seller.sellerName}
                    </Heading>
                    <Text className="text-gray-600 text-sm flex items-center">
                      <ImLocation className="inline mr-1" size={14} />
                      {seller.address}
                    </Text>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <Box className="bg-blue-50 p-2 rounded-lg">
                    <Text fontSize="sm" className="text-gray-700">
                      <span className="font-semibold">Listings:</span>{" "}
                      {seller.listingsCount || "N/A"}
                    </Text>
                  </Box>
                  <Box className="bg-blue-50 p-2 rounded-lg">
                    <Text fontSize="sm" className="text-gray-700">
                      <span className="font-semibold">Rating:</span>{" "}
                      {seller.rating || "N/A"}
                    </Text>
                  </Box>
                </div>

                <Link to={`/seller-profile/${seller.id}`}>
                  <Button
                    width="full"
                    className="transition-all duration-300 transform hover:scale-[1.02] hover:shadow-md"
                    bgGradient="linear(to-r, blue.500, blue.600)"
                    color="white"
                    _hover={{
                      bgGradient: "linear(to-r, blue.600, blue.700)",
                      transform: "translateY(-1px)",
                      boxShadow: "md",
                    }}
                    _active={{
                      bgGradient: "linear(to-r, blue.700, blue.800)",
                    }}
                    size="md"
                    borderRadius="lg"
                    boxShadow="sm"
                    rightIcon={<FaStore size={14} />}
                  >
                    Visit Showroom
                  </Button>
                </Link>
              </div>
            </Box>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SeeAllFeaturedSellers;
